/**
 * Zeit rechnen (S62; 25-zeitsteuerung.md § 7.1): when a section of a card or a folder applies, in the
 * local time of the business (Europe/Berlin, the only one for now), the same on the server and in the
 * app. A window is weekdays and a range of minutes after midnight; a range that ends at or before its
 * start runs over midnight and belongs to the day it begins (§ 7.8). Spring forward: a time that does
 * not exist (02:30) is the next one that does (03:00); fall back: the first of the two.
 */
import { TIME_WORDS, clockText, daysText, rangeText } from "@household-kit/lang";
import type { Lang } from "./translation.ts";

/** 0 = Monday … 6 = Sunday. */
export type Weekday = 0 | 1 | 2 | 3 | 4 | 5 | 6;

/** Weekdays and a time of day, from and to in minutes after midnight (to 1440 = 24:00). */
export interface Window {
  days: Weekday[];
  from: number;
  to: number;
}

export const ZONE = "Europe/Berlin";

/**
 * Die Zeitsteuerung des Betriebs (S62, Nachtrag drei Wahlen; § 2 und Z0 des Entwurfs): „Aus“ (the
 * default: the app of before, not one new button), „Standard“ (sections at times on the net, changes
 * planned for later), „Erweitert“ (folders, the weekly plan, exceptions; Standard with it).
 */
export const TIMINGS = ["off", "standard", "extended"] as const;
export type Timing = (typeof TIMINGS)[number];
/** Whether a business's choice has at least that level: („extended“, „standard“) → true. */
export const timingAtLeast = (have: Timing | undefined, level: Timing) => TIMINGS.indexOf(have ?? "off") >= TIMINGS.indexOf(level);
/** The choices in the account with their explanation (Martin, 03.11.2026: „jeweils mit einer Erklärung“). */
export const TIMING_CHOICES: readonly { id: Timing; label: string; text: string }[] = [
  { id: "off", label: "Aus", text: "Eine Karte für den ganzen Tag. Speise- und Empfehlungskarte wechselst du von Hand, es gilt sofort." },
  { id: "standard", label: "Standard", text: "Einzelne Abschnitte nur zu bestimmten Zeiten zeigen, etwa den Mittagstisch Mo–Fr 11:30–14:30. Und Karten zu einem Zeitpunkt wechseln: die Empfehlung ab morgen 6 Uhr, die Winterkarte ab 1.12." },
  { id: "extended", label: "Erweitert", text: "Zu verschiedenen Zeiten ganz verschiedene Karten zeigen: mittags die Mittagskarte, abends die Hauptkarte, sonntags Brunch, mit Ruhetag und Ausnahmen für Feiertage. Alles aus Standard gehört dazu." },
];
export const DAY_MS = 86_400_000;

const parts = new Intl.DateTimeFormat("en-GB", { timeZone: ZONE, year: "numeric", month: "2-digit", day: "2-digit", hour: "2-digit", minute: "2-digit", weekday: "short", hourCycle: "h23" });
const WEEKDAYS = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

/** A moment in the business's local time: its date („2026-11-04“), weekday and minute of the day. */
export interface Local {
  date: string;
  weekday: Weekday;
  minute: number;
}

export function localOf(at: number): Local {
  const p = Object.fromEntries(parts.formatToParts(new Date(at)).map((x) => [x.type, x.value]));
  return { date: `${p.year}-${p.month}-${p.day}`, weekday: WEEKDAYS.indexOf(p.weekday!) as Weekday, minute: Number(p.hour) * 60 + Number(p.minute) };
}

/** The weekday of a local date. */
export const weekdayOf = (date: string): Weekday => ((new Date(`${date}T12:00:00Z`).getUTCDay() + 6) % 7) as Weekday;

/** A local date `days` later: („2026-11-04“, 1) → „2026-11-05“. */
export function dateAfter(date: string, days: number): string {
  const d = new Date(`${date}T12:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

/**
 * The moment a local date and minute is (S62 § 7.8): in spring the minutes that do not exist count as
 * the first that does (02:30 → 03:00), in autumn the first of the two.
 */
export function atLocal(date: string, minute: number): number {
  if (minute >= 1440) return atLocal(dateAfter(date, Math.floor(minute / 1440)), minute % 1440);
  const [y, m, d] = date.split("-").map(Number) as [number, number, number];
  for (let k = 0; k <= 120; k++) {
    const naive = Date.UTC(y, m - 1, d, 0, minute + k);
    // Summer time first: in autumn the first of the two.
    for (const t of [naive - 2 * 3_600_000, naive - 3_600_000]) {
      const l = localOf(t);
      if (l.date === date && l.minute === minute + k) return t;
    }
  }
  return Date.UTC(y, m - 1, d, 0, minute) - 3_600_000;
}

/** Whether a window has a day and the minutes of a moment; over midnight the minutes after it belong to the day before. */
export function inWindow(w: Window, at: number): boolean {
  const l = localOf(at);
  if (w.from < w.to) return w.days.includes(l.weekday) && l.minute >= w.from && l.minute < w.to;
  const before = ((l.weekday + 6) % 7) as Weekday;
  return (w.days.includes(l.weekday) && l.minute >= w.from) || (w.days.includes(before) && l.minute < w.to);
}

/** Where a window begins and ends on a local date it has (its end on the next day when it runs over midnight). */
export function spanOn(w: Window, date: string): { start: number; end: number } | null {
  if (!w.days.includes(weekdayOf(date))) return null;
  const start = atLocal(date, w.from);
  const end = w.from < w.to ? (w.to === 1440 ? atLocal(dateAfter(date, 1), 0) : atLocal(date, w.to)) : atLocal(dateAfter(date, 1), w.to);
  return { start, end };
}

/** The next moment after `at` where one of the windows begins or ends, within a week and a day; null: none. */
export function nextChange(windows: readonly Window[], at: number): number | null {
  const today = localOf(at).date;
  let best: number | null = null;
  for (let k = -1; k <= 8; k++) {
    const date = dateAfter(today, k);
    for (const w of windows) {
      const s = spanOn(w, date);
      if (!s) continue;
      for (const t of [s.start, s.end]) if (t > at && (best === null || t < best)) best = t;
    }
  }
  return best;
}

/** A window as a card writes it: „Mo–Fr 11:30–14:30“, „täglich 17:00–23:00“, in the guest's language. */
export const windowText = (w: Window, lang: Lang | "de" = "de") => `${daysText(w.days, lang)} ${rangeText(w.from, w.to, lang)}`.trim();

/** The pieces of a local day a window covers, in minutes from 0 to 1440, for the bar of the day (§ 6.1). */
export function dayBar(w: Window, date: string): { from: number; to: number }[] {
  const day = weekdayOf(date);
  const before = ((day + 6) % 7) as Weekday;
  const out: { from: number; to: number }[] = [];
  if (w.from >= w.to && w.days.includes(before) && w.to > 0) out.push({ from: 0, to: w.to });
  if (w.days.includes(day)) out.push(w.from < w.to ? { from: w.from, to: w.to } : { from: w.from, to: 1440 });
  return out;
}

/** A window as the app sends it, or null: days 0–6 (at least one), from and to whole minutes 0–1440, not the same. */
export function cleanWindow(v: unknown): Window | null {
  if (!v || typeof v !== "object") return null;
  const o = v as Record<string, unknown>;
  const days = Array.isArray(o.days) ? [...new Set(o.days.filter((d): d is Weekday => Number.isInteger(d) && (d as number) >= 0 && (d as number) <= 6))].sort((a, b) => a - b) : [];
  const from = Number(o.from);
  const to = Number(o.to);
  if (!days.length || !Number.isInteger(from) || !Number.isInteger(to) || from < 0 || from > 1439 || to < 0 || to > 1440 || from === to) return null;
  return { days, from, to };
}

/** „Heute geschlossen“ and the other words of times (household-kit `lang`, H15), for the app's own use. */
export { TIME_WORDS, clockText, daysText, rangeText };
